import i18n from 'i18next';
import {TaskCode} from './tasks';
export const fieldLengthLimit = 100;
export const validationRegex = /[^a-zA-Z @\.0-9]/;
export const phoneNumberReplaceRegex = /[\(\) \-_]*/g;
export const inactivityTimeout = 1200000;
export const sysAdminGroupSlug = 'SystemAdministrator';
export const firstPage = 1;
export const patientIdLengthLimit = 20;
export const userNameLengthLimit = 64;
export const operatoryNumberLimit = 10;
export const treatmentCodeLengthLimit = 10;
export const longFieldLengthLimit = 250;
export const shortDateFormat = 'MMM D, YYYY';
export const dateFormat = 'MMM D, YYYY h:mm a';
export const filtersDatePickerDateFormat = 'MMM d, yyyy h:mm a';
export const datePickerDateFormatMMMDYYYY = 'MMM d, yyyy';
export const dateFormatDDMMYYYY = 'DD/MM/YYYY';
export const dateFormatYYYYMMDD = 'YYYY-MM-DD';
export const profileChangePassPolicy = 'b2c_1a_profile_edit_password_change';
export const resetPassPolicy = 'b2c_1a_pwd_reset';
export const emailChangePolicy = 'b2c_1a_change_email';
export const notRunningDelay = 1000;
export const runningDelay = 20000;
export const defaultDelay = 0;
export const defaultTasksCount= 0;

export const phoneNumberMask = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/
];

export const keyCodes = {
  ESCAPE: 27,
  ENTER: 13
};

export const validation = {
  alphaNumeric: /^[a-zA-Z0-9]*$/,
  tenantName: /^[a-zA-Z0-9 ]*$/,
  email: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
  userName: /^[a-zA-Z`'"^.,-]+( [a-zA-Z`'"^.,-]+)*$/,
  phoneNumber: /^[0-9\(\)\- ]*$/,
  textField: /^[a-zA-Z0-9 ]*$/,
  numeric: /^[0-9]*$/,
  hours: /^[1-9][0-9]?[0-9]?$/,
  hoursWithMinutes: /^[0-9][0-9]?[0-9]?$/,
  minutesWithHours: /^[0-5]?[0-9]$/,
  minutesWithoutHours: /^[1-5]?[0-9]$/,
  minutes: /^[1-9][0-9]?[0-9]?$/,
  seconds: /^[0-5]?[0-9]$/,
  percent: /^[1-9][0-9]?$|^100$/
};

export const validationErrors = {
  required: i18n.t('validation:required'),
  shortRequired: i18n.t('validation:shortRequired'),
  latin: i18n.t('validation:latin'),
  textField: i18n.t('validation:textField'),
  validValue: i18n.t('validation:validValue'),
  alphaNumeric: i18n.t('validation:alphaNumeric'),
  userName: i18n.t('validation:userName'),
  email: i18n.t('validation:email'),
  phoneNumber: i18n.t('validation:phoneNumber'),
  maxLength: (max) => i18n.t('validation:maxLength', {max}),
  numeric: i18n.t('validation:numeric'),
  patientId: i18n.t('validation:patientId'),
  hours: i18n.t('validation:hours'),
  hoursWithMinutes: i18n.t('validation:hoursWithMinutes'),
  minutesWithHours: i18n.t('validation:minutesWithHours'),
  minutesWithoutHours: i18n.t('validation:minutesWithoutHours'),
  minutes: i18n.t('validation:minutes'),
  seconds: i18n.t('validation:seconds'),
  percent: i18n.t('validation:percent'),
  select: i18n.t('validation:select'),
  dateTime: i18n.t('validation:dateTime')
};

export const errorMessages = {
  SLUG_UNIQUENESS_CONSTRAINT: (entity: string) =>
    `The ${entity} with such id already exists`,
  NAME_UNIQUENESS_CONSTRAINT: (entity: string) =>
    `The ${entity} with such name already exists`,
  EMAIL_VALIDATION_CONSTRAINT: 'Please type a correct email',
  NAME_VALIDATION_CONSTRAINT: 'Please type a correct name',
  EMAIL_UNIQUENESS_CONSTRAINT: (entity: string) =>
    `The ${entity} with such email already exists`,
  SLUG_VALIDATION_CONSTRAINT: (entity: string) =>
    `Please enter valid ${entity} id`,
  DEFAULT: 'Something went wrong. Please try again later.',
  NO_PERMISSIONS: 'You don\'t have enough permissions to see this page',
  DESCRIPTION_UNIQUENESS_CONSTRAINT: 'Description must be unique',
  DESCRIPTION_VALIDATION_CONSTRAINT: 'Please type a correct description',
  NUMBER_UNIQUENESS_CONSTRAINT: 'Operatory number must be unique',
  NUMBER_VALIDATION_CONSTRAINT: 'Please use no more than 10 symbols',
  CODE_UNIQUENESS_CONSTRAINT: (entity: string) =>
    `The ${entity} with such code already exists`,
  USER_ALREADY_EXISTS_IN_TENANT: 'User already exists in this tenant'
};

export const processesNames = {
  'SCHEDULING': 'Scheduling',
  'CHECK_IN': 'Check-In',
  'CLINICAL_EVALUATION': 'Clinical Evaluation',
  'TREATMENT_CARE': 'Treatment & Care',
  'CHECKOUT': 'Check-Out',
  'INVESTIGATE': 'Investigation',
  'EMPLOYEE_SURVEY': 'Employee survey'
};

export enum Subprocess {
  TERMINATE = 'TERMINATE',
  RESCHEDULE_APPOINTMENT = 'RESCHEDULE_APPOINTMENT',
  EARLY_ARRIVAL = 'EARLY_ARRIVAL'
}

export enum ProcessActions {
  CLAIM = 'CLAIM',
  RECLAIM = 'RECLAIM',
  DISCARD = 'DISCARD',
}

export const getSubprocessPopupMessage = (
    subprocess: Subprocess,
    subprocessName: string
) => {
  switch (subprocess) {
    case Subprocess.TERMINATE:
      return `The ${subprocessName} process will be terminated.`;
    case Subprocess.RESCHEDULE_APPOINTMENT:
      return `The ${subprocessName} process appointment will be rescheduled.`;
    case Subprocess.EARLY_ARRIVAL:
      return `The ${subprocessName} process will be early arrived.`;
    default:
      return '';
  }
};

export const getSubprocessToastMessage = (
    subprocess: Subprocess,
    subprocessName: string,
    patientName: string
) => {
  switch (subprocess) {
    case Subprocess.TERMINATE:
      return i18n.t('processes:subprocessToastMessage.terminated', {subprocessName, patientName});
    case Subprocess.RESCHEDULE_APPOINTMENT:
      return i18n.t('processes:subprocessToastMessage.rescheduled', {subprocessName, patientName});
    case Subprocess.EARLY_ARRIVAL:
      return i18n.t('processes:subprocessToastMessage.earlyArrived', {subprocessName, patientName});
    default:
      return '';
  }
};

export const getProcessActionsPopupMessage = (
    processAction: ProcessActions
) => {
  switch (processAction) {
    case ProcessActions.CLAIM:
      return i18n.t('processes:processAssignee.popupMessage.claim');
    case ProcessActions.DISCARD:
      return i18n.t('processes:processAssignee.popupMessage.discard');
    case ProcessActions.RECLAIM:
      return i18n.t('processes:processAssignee.popupMessage.reclaim');
    default:
      return '';
  }
};

export const getProcessActionsToastMessage = (
    processAction: ProcessActions
) => {
  switch (processAction) {
    case ProcessActions.CLAIM:
      return i18n.t('processes:processAssignee.toastMessage.claim');
    case ProcessActions.DISCARD:
      return i18n.t('processes:processAssignee.toastMessage.discard');
    case ProcessActions.RECLAIM:
      return i18n.t('processes:processAssignee.toastMessage.reclaim');
    default:
      return '';
  }
};

export const PROCESS_SORTING_NAMES = {
  patientAsc: 'PATIENT_ASC',
  patientDesc: 'PATIENT_DESC'
};

export const PROCESS_SORTING_QUERY_PARAMS = {
  PATIENT_ASC: {
    direction: 'asc',
    sort: 'patient'
  },
  PATIENT_DESC: {
    direction: 'desc',
    sort: 'patient'
  }
};

export const PROCESS_SORTING_OPTIONS = [
  {
    value: PROCESS_SORTING_NAMES.patientAsc,
    label: 'Patient Name Ascending'
  },
  {
    value: PROCESS_SORTING_NAMES.patientDesc,
    label: 'Patient Name Descending'
  }
];

export const SCHEDULING_PROCESSES_DEFAULT_QUICK_FILTER = {
  value: 'ALL',
  label: i18n.t('processes:schedulingProcessQuickFilterLabels.all')
};

export const SCHEDULING_PROCESSES_QUICK_FILTERS = [
  SCHEDULING_PROCESSES_DEFAULT_QUICK_FILTER,
  {
    value: TaskCode.CONFIRM_APPOINTMENT,
    label: i18n.t('processes:schedulingProcessQuickFilterLabels.confirmAppointment')
  },
  {
    value: TaskCode.FORWARD_INTAKE_CALL_SHEET,
    label: i18n.t('processes:schedulingProcessQuickFilterLabels.forwardIntakeCallSheetList')
  }
];

export const noItemsMessages = {
  ITEMS: 'No items',
  SEARCH: 'No results found',
  FILTER: 'No results found. Please check your filters'
};

export const MINUTES_IN_HOUR = 60;
export const SECONDS_IN_MINUTE = 60;
export const MILLISECONDS_IN_SECOND = 1000;
export const MILLISECONDS_IN_MINUTE =
    SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;
export const SECONDS_IN_HOUR = MINUTES_IN_HOUR * SECONDS_IN_MINUTE;

export enum SnackbarVariants {
  SUCCESS = 'success',
  ERROR = 'error',
  DEFAULT = 'default'
}
